import {
  Component,
  computed,
  inject,
  input,
  output,
  type OnInit,
} from '@angular/core';
import type { TypedComponentRendering } from '@core/jss-models';
import { Status } from '@essent/common';
import type { InstallationAddress } from '@essent/installation-address';
import type { FullCalculateFormValues } from '@innogy/become-a-customer/shared';
import {
  CalculateComponentContent,
  type CalculateComponentRendering,
} from '@innogy/become-a-customer/shared/interfaces';
import {
  disableCalculateCrntHouseNumberAdditionDropdownAction,
  enableCalculateManualHouseNumberAdditionInputAction,
} from '@innogy/become-a-customer/state';
import {
  DropdownComponent,
  InputErrorComponent,
  NumberComponent,
  TextComponent,
  type DropdownItem,
} from '@innogy/common-ui/forms';
import {
  uppercaseConverter,
  AutocompleteDirective,
  IsInvalidPipe,
  stringNumberConverterForLocale,
} from '@innogy/utils-deprecated';
import { Store } from '@ngrx/store';
import {
  FormGroupControls,
  type FormGroupState,
  NgrxFormsModule,
} from 'ngrx-forms';
import {
  TrackLinkDirective,
  TrackValueChangeDirective,
} from '@analytics/events';
import { CommonModule } from '@angular/common';
import { LoadingComponent } from '@legacy/common-ui-shared';
import { InfoCardComponent } from '@legacy/common-ui-utility-components';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import {
  CrntIcon,
  CrntFormControl,
  CrntTextInput,
  CrntFormControlLabel,
  CrntSelect,
} from '@essent/current-web-angular';

@Component({
  selector: 'wl-calculate-form-address',
  templateUrl: './calculate-form-address.component.html',
  styleUrls: ['./calculate-form-address.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AutomationIdDirective,
    AutocompleteDirective,
    IsInvalidPipe,
    TrackLinkDirective,
    TrackValueChangeDirective,
    CrntIcon,
    InfoCardComponent,
    LoadingComponent,
    TextComponent,
    NumberComponent,
    DropdownComponent,
    InputErrorComponent,
    CrntFormControl,
    CrntTextInput,
    NgrxFormsModule,
    CrntFormControlLabel,
    CrntSelect,
  ],
})
export class CalculateFormAddressComponent implements OnInit {
  public readonly uppercaseConverter = uppercaseConverter;
  public readonly _stringNumberConverter =
    stringNumberConverterForLocale('nl-NL');

  readonly store = inject(Store);

  readonly rendering =
    input<TypedComponentRendering<CalculateComponentRendering>>();
  readonly content = input.required<CalculateComponentContent>();
  readonly controls =
    input.required<FormGroupControls<FullCalculateFormValues>>();
  readonly formState =
    input.required<FormGroupState<FullCalculateFormValues>>();
  readonly houseNumberAdditions = input<DropdownItem[]>();
  readonly manualHouseNumberAdditionInput = input<boolean>();
  readonly installationAddressStatus = input<Status>();
  readonly installationAddressNotFound = input<boolean>();
  readonly isAddressManualTriggerActive = input<boolean>();
  readonly isAlternateAddressEntryEnabled = input<boolean>();
  readonly installationAddresses = input<InstallationAddress[]>();

  hideManualHouseNumberAdditionInput = output();
  setAddressNextButtonClicked = output();
  updateAddressButtonClicked = output();

  readonly showLoader = computed(
    () => this.installationAddressStatus() === Status.PENDING
  );

  readonly enableCrntHouseNumberAddition = computed(
    () =>
      this.controls().postalCode.isValid && this.controls().houseNumber?.isValid
  );

  readonly showProvidedInstallationAddress = computed(() => {
    const address = this.installationAddresses()?.[0];
    const controls = this.controls();
    return address
      ? `${address.street} ${address.houseNumber}${controls.houseNumberAddition.value ?? ''}, ${address.city}`.trim()
      : `${controls.postalCode.value ?? ''}, ${controls.houseNumber?.value ?? ''} ${controls.houseNumberAddition.value ?? ''}`.trim();
  });

  /**
   * Should only show the message when the message has been filled in in Sitecore (and when the API returns a 404)
   */
  readonly showAddressNotFoundMessage = computed(() => {
    return (
      this.installationAddressNotFound() &&
      !!(
        this.content().addressNotFoundErrorTitle ||
        this.content().addressNotFoundErrorMessage
      )
    );
  });

  ngOnInit() {
    if (
      this.content().useSupervisedManualHouseNumberAddition &&
      !this.content().useAccessibleCalculator
    ) {
      this.store.dispatch(
        enableCalculateManualHouseNumberAdditionInputAction()
      );
    }

    if (this.content().useAccessibleCalculator) {
      this.store.dispatch(
        disableCalculateCrntHouseNumberAdditionDropdownAction()
      );
    }
  }

  readonly submitButtonState = computed(() =>
    this.installationAddressStatus() === Status.PENDING ? 'disabled' : 'enabled'
  );

  readonly postalCodeInvalid = computed(
    () =>
      this.controls().postalCode.isTouched &&
      this.controls().postalCode.isInvalid
  );

  readonly houseNumberInvalid = computed(
    () =>
      !!this.controls().houseNumber?.isTouched &&
      !!this.controls().houseNumber?.isInvalid &&
      !!this.controls().houseNumber?.isDirty
  );
}
